import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useLocation } from 'wouter';

import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import EmailIcon from '@mui/icons-material/Email';

import clientSelector from '../clientSelector';
import { loadClients, editClient, loadClientById, clearCurrentClient } from '../clientsReducer';
import type { Client as ClientType } from '../../common/types/client';
import type { DecryptedItem } from '../../common/types/items';
import transformer from '../../common/helpers/transformer';
import ClientForm from './ClientForm';
import type { RootState, AppDispatch } from '../../store';
import { getClientPayloadType } from '../../common/helpers/payloadType';

type Props = {
  clientId: string;
};

function Client({ clientId }: Props) {
  const dispatch: AppDispatch = useDispatch();
  const [location] = useLocation();
  const [editedClient, updateEditedClient] = React.useState<ClientType | null | undefined>(null);

  const status = useSelector(clientSelector.getStatus);
  const item = useSelector<RootState, DecryptedItem<ClientType> | null>(clientSelector.getCurrentClient);

  const payloadType = getClientPayloadType(location);
  const client = item != null ? item.payload : null;

  const saveClient = async (clientToSave: ClientType) => {
    const payload = await transformer.fromObjectToBase64WithEncryption(clientToSave);
    await dispatch(editClient({ id: clientId, payload }));
    await dispatch(loadClients(payloadType));
  };

  React.useEffect(() => {
    dispatch(loadClientById(clientId));
    return () => {
      dispatch(clearCurrentClient());
    };
  }, [clientId]);

  React.useEffect(() => {
    if (item != null && editedClient == null) {
      updateEditedClient(client);
    }
  }, [client]);

  return (
    <Stack height='100%' overflow='hidden'>
      <Stack id='client-header' px={3} pt={3} pb={1} direction='row' justifyContent='space-between'>
        <Stack direction='row' alignItems='center'>
          <Typography fontSize='1.5rem' mr={4}>
            Klient*in
            {client != null && client.name != null ? `: ${client.name}` : ''}
          </Typography>

          {client != null && client.email != null && (
            <Link mr={4} display='flex' href={`mailto:${client.email}`} aria-label='email client' underline='none'>
              <EmailIcon color='primary' sx={{ fontSize: '30px' }} />
            </Link>
          )}
        </Stack>
        <Button
          disabled={status === 'loading'}
          variant='contained'
          onClick={async () => {
            if (editedClient != null) {
              saveClient(editedClient);
            }
          }}
        >
          Speichern
        </Button>
      </Stack>
      {editedClient != null && (
        <ClientForm
          clientId={clientId}
          client={editedClient}
          updateClient={updateEditedClient}
          saveClient={saveClient}
        />
      )}
    </Stack>
  );
}

export default Client;
